import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import parse, { domToReact, attributesToProps, Element } from 'html-react-parser';

const StoryPoint = ({ title, body, image, reverse = false, bgcolor = 'grey.200' }) => {
  const theme = useTheme();
  const isWideScreen = useMediaQuery('(min-width:1000px)');

  // Custom options for html-react-parser to enhance security and styling
  const parseOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && domNode.attribs) {
        // Handle specific HTML tags to use MUI components when appropriate
        switch (domNode.name) {
          case 'a':
            const props = attributesToProps(domNode.attribs);
            return (
              <Typography 
                component="a"
                variant="inherit" 
                {...props}
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {domToReact(domNode.children, parseOptions)}
              </Typography>
            );
          default:
            return undefined; // Let html-react-parser handle other elements
        }
      }
    }
  };

  // Align text if we flip columns
  const textAlign = reverse ? 'right' : 'left';

  return (
    <Box 
      sx={{ px: { xs: 2, md: 10, my: 6 } }}
      bgcolor={bgcolor}
      py={4}
      >
      {/* ⬇️  One container that toggles between block & flex */}
      <Box
        sx={{
          display: isWideScreen ? 'flex' : 'block',
          flexDirection: reverse ? 'row-reverse' : 'row',
          alignItems: 'center',
          gap: { xs: 3, md: 6 },
        }}
      >
        {/* TEXT COLUMN */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{
              mt: 2,
              mb: 3,
              mr: { xs: 0, md: reverse ? 0 : 2 },
              color: 'text.secondary',
              maxWidth: { xs: '100%'},
              textAlign: "left",
            }}
          >
            {title}
          </Typography>
          
          <Box
            component="div"
            sx={{ 
              typography: 'body1',
              color: 'text.primary',
              textAlign: 'left',
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              },
              '& p': {
                marginBottom: '1rem'
              },
              '& ul, & ol': {
                paddingLeft: '1.5rem',
                marginBottom: '1rem',
                '& li': {
                  marginBottom: '0.5rem'
                }
              }
            }}
          >
            {parse(body, parseOptions)}
          </Box>
        </Box>

        {/* IMAGE COLUMN */}
        <Box
          sx={{
            flex: { xs: 'none', md: '0 0 45%' }, // 45 % width on desktop
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            my: { xs: 3, md: 0 },
            maxWidth: 500,
            maxHeight: 500,
          }}
        >
          {image}
        </Box>
      </Box>
    </Box>
  );
};

export default StoryPoint;